import type {
    TechnologiesBaseListItem,
    TechnologiesConditionEntry,
    WithNamesMatchType,
    WithSecondaryName,
} from "../models/filters/technology/types";
import { TechnologiesConditionTypeEnum } from "../models/filters/technology/types";

import { compareNamesAsc, getMatchedSubString, stringMatchesSearchTerm } from "./common";

/**
 * Returns a predicate to check whether given technologies condition has given type.
 * @param type
 */
export function isConditionOfType(type: TechnologiesConditionTypeEnum) {
    return function conditionIsOfTypePredicate(entry: TechnologiesConditionEntry) {
        return entry.type === type;
    };
}

/**
 * Returns a condition factory function with given condition type applied
 * @param type
 */
export function getEntryWithTypeFactory(type: TechnologiesConditionTypeEnum) {
    return function createEntryWithName(name: string): TechnologiesConditionEntry {
        return { name, type };
    };
}

/**
 * Returns a predicate to check whether two condition entries match
 * @param given
 */
export function doesEntryNotMatchGiven(given: TechnologiesConditionEntry) {
    return (entry: TechnologiesConditionEntry) => {
        return given.name !== entry.name || given.type !== entry.type;
    };
}

/**
 * TODO: Can be replaced with common property accessor helper
 * Returns `name` property of given condition entry.
 * @param entry
 */
function getName(entry: TechnologiesConditionEntry) {
    return entry.name;
}

/**
 * Maps given entries to their names
 * @param entries
 */
export function getNames(entries: TechnologiesConditionEntry[]) {
    return entries.map(getName);
}

/**
 * Predicate function that checks if given entry is of type `category`
 */
export const isOfTypeCategory = isConditionOfType(TechnologiesConditionTypeEnum.Category);

/**
 * Predicate function that checks if given entry is of type `subCategory`
 */
export const isOfTypeSubCategory = isConditionOfType(TechnologiesConditionTypeEnum.SubCategory);

/**
 * Predicate function that checks if given entry is of type `technology`
 */
export const isOfTypeTechnology = isConditionOfType(TechnologiesConditionTypeEnum.Technology);

/**
 * Keeps only entries of type `category`
 * @param entries
 */
function getCategoriesEntries(entries: readonly TechnologiesConditionEntry[]) {
    return entries.filter(isOfTypeCategory);
}

/**
 * Keeps only entries of type `subCategory`
 * @param entries
 */
function getSubCategoriesEntries(entries: readonly TechnologiesConditionEntry[]) {
    return entries.filter(isOfTypeSubCategory);
}

/**
 * Keeps only entries of type `technology`
 * @param entries
 */
function getTechnologiesEntries(entries: readonly TechnologiesConditionEntry[]) {
    return entries.filter(isOfTypeTechnology);
}

/**
 * Returns list of `name`s of entries of type `category`
 * @param entries
 */
export function getCategoriesEntriesNames(entries: readonly TechnologiesConditionEntry[]) {
    return getNames(getCategoriesEntries(entries));
}

/**
 * Returns list of `name`s of entries of type `subCategory`
 * @param entries
 */
export function getSubCategoriesEntriesNames(entries: readonly TechnologiesConditionEntry[]) {
    return getNames(getSubCategoriesEntries(entries));
}

/**
 * Returns list of `name`s of entries of type `technology`
 * @param entries
 */
export function getTechnologiesEntriesNames(entries: readonly TechnologiesConditionEntry[]) {
    return getNames(getTechnologiesEntries(entries));
}

/**
 * Sort predicate function. Those objects that has a `nameMatch` come first.
 * @param a
 * @param b
 */
export function compareByPrimaryNameMatch<T extends TechnologiesBaseListItem & Pick<WithNamesMatchType, "nameMatch">>(
    a: T,
    b: T,
) {
    if (a.nameMatch && !b.nameMatch) {
        return -1;
    }

    return compareNamesAsc(a, b);
}

/**
 * Checks whether given technology list `item` matches given `searchTerm`
 * @param searchTerm
 */
export function nameMatchesSearchTerm(searchTerm: string) {
    return function <T extends TechnologiesBaseListItem & Partial<WithSecondaryName>>(item: T) {
        if (searchTerm.length === 0) {
            return true;
        }

        const nameMatches = stringMatchesSearchTerm(item.name, searchTerm);
        let secondaryNameMatches = false;

        if (typeof item.secondaryName !== "undefined") {
            secondaryNameMatches = stringMatchesSearchTerm(item.secondaryName, searchTerm);
        }

        return nameMatches || secondaryNameMatches;
    };
}

/**
 * Populates given `item` with `nameMatch` and `secondaryNameMatch` properties if match is found
 * @param searchTerm
 */
export function toItemWithMatchedSubStrings(searchTerm: string) {
    return function <T extends TechnologiesBaseListItem & Partial<WithSecondaryName>>(
        item: T,
    ): T & WithNamesMatchType & { isSelected?: boolean } {
        if (searchTerm.length === 0) {
            return item;
        }

        const nameMatch = getMatchedSubString(searchTerm, item.name);
        const secondaryNameMatch = getMatchedSubString(searchTerm, item.secondaryName);

        return {
            ...item,
            nameMatch,
            secondaryNameMatch,
        };
    };
}
